import {
    getDocs,
    collection,
    query,
    where,
    updateDoc,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    limit,
    startAfter,
    documentId,
    Timestamp,
    addDoc,
} from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchBranchBankId } from '../../../branches/service/branchesService';

export type LeaseRenewalSurvey = {
    id: string;
    surveyId: string; // LRS-XXXXX
    createdBy: string;
    createdByName?: string;
    createdAt?: any;
    status: 'Assigned' | 'Submitted';
    bankId: string;

    // Filled by Incharge_MIS on creation
    branchCode: string;
    branchName: string; // branch address
    branchLocation: string; // location/city
    assignedTo: string; // PSO UID
    assignedToName: string;
    requestDate: string;

    // Filled by PSO on submission
    renewalPeriod?: string; // "5 Years" etc.
    addressOfBranch?: string;
    branchType?: string;
    hasInfrastructure?: boolean;
    infrastructureDetails?: Record<string, boolean>;
    rccStructured?: boolean;
    isolatedLocation?: boolean;
    weakSecurity?: boolean;
    floodArea?: boolean;
    publicNuisance?: boolean;
    criticalRiskBuilding?: boolean;
    fuelStationNearby?: boolean;
    suitableForExtension?: boolean;
    remarks?: string; // Detail Rationale for Relocation / Others
    surveyDate?: string;
    surveySubmittedOn?: string;
};

type SurveysMap = Map<string, LeaseRenewalSurvey>;

const sortSurveys = (surveys: LeaseRenewalSurvey[]) => {
    return surveys.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
    });
};

export function subscribeToLeaseRenewalSurveys(onUpdate: (surveys: LeaseRenewalSurvey[]) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return () => {};
    }

    let disposed = false;
    const unsubscribers: Array<() => void> = [];
    const surveysMap: SurveysMap = new Map();

    const emit = () => {
        if (disposed) return;
        onUpdate(sortSurveys(Array.from(surveysMap.values())));
    };

    const attachListener = (queryRef: any) => {
        const unsubscribe = onSnapshot(queryRef, (snapshot: any) => {
            snapshot.docChanges().forEach((change: any) => {
                const data = change.doc.data();

                if (change.type === 'removed') {
                    surveysMap.delete(change.doc.id);
                } else {
                    surveysMap.set(change.doc.id, {
                        id: change.doc.id,
                        ...data,
                    } as LeaseRenewalSurvey);
                }
            });

            emit();
        }, (error: any) => {
            console.error('Error listening to lease renewal surveys:', error);
        });

        unsubscribers.push(unsubscribe);
    };

    (async () => {
        let bankId: string | null = sessionStorage.getItem('bankDocId');
        if (!bankId) {
            try {
                const directBank = await getDoc(doc(db, "banks", uid));
                if (directBank.exists()) {
                    bankId = uid;
                } else {
                    const banksSnap = await getDocs(collection(db, "banks"));
                    for (const bankDoc of banksSnap.docs) {
                        const bankData = bankDoc.data();
                        if (bankData.userId === uid) {
                            bankId = bankDoc.id;
                            break;
                        }
                    }
                }
            } catch (err) {
                console.error("Error resolving bankDocId for bank user", err);
            }
        }
        if (!bankId) {
            try {
                bankId = await fetchBranchBankId(uid);
            } catch (error) {
                console.error('Error resolving bank ID for surveys:', error);
            }
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Lease renewal subscription aborted.');
            return;
        }

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');
        const isBankUser = !designation;

        const surveysRef = collection(db, 'banks', bankId, 'leaseRenewalSurveys');

        if (isMIS || isBankUser) {
            attachListener(surveysRef);
        } else {
            // Strict data isolation: PSO can see only if they created it or if it is assigned to them
            attachListener(query(surveysRef, where('createdBy', '==', uid)));
            attachListener(query(surveysRef, where('assignedTo', '==', uid)));

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    managedIds.forEach((managedId) => {
                        attachListener(query(surveysRef, where('createdBy', '==', managedId)));
                        attachListener(query(surveysRef, where('assignedTo', '==', managedId)));
                    });
                } catch (error) {
                    console.error('Error fetching managed PSO lease surveys:', error);
                }
            }
        }
    })();

    return () => {
        disposed = true;
        unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
}

export async function fetchLeaseRenewalSurveys(): Promise<LeaseRenewalSurvey[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        const surveysMap = new Map<string, LeaseRenewalSurvey>();

        const addSnapshotToMap = (snapshot: any, bankFilter?: string | null) => {
            snapshot.forEach((docSnap: any) => {
                const data = docSnap.data();
                const docBankId = data.bankId ?? null;

                if (bankFilter && docBankId && docBankId !== bankFilter) {
                    return;
                }

                surveysMap.set(docSnap.id, {
                    id: docSnap.id,
                    ...data,
                } as LeaseRenewalSurvey);
            });
        };

        let bankId: string | null = sessionStorage.getItem('bankDocId');
        if (!bankId) {
            try {
                const directBank = await getDoc(doc(db, "banks", uid));
                if (directBank.exists()) {
                    bankId = uid;
                } else {
                    const banksSnap = await getDocs(collection(db, "banks"));
                    for (const bankDoc of banksSnap.docs) {
                        const bankData = bankDoc.data();
                        if (bankData.userId === uid) {
                            bankId = bankDoc.id;
                            break;
                        }
                    }
                }
            } catch (err) {
                console.error("Error resolving bankDocId for bank user", err);
            }
        }
        if (!bankId) {
            try {
                bankId = await fetchBranchBankId(uid);
            } catch (error) {
                console.error('Error resolving bank ID for surveys:', error);
            }
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Cannot fetch lease surveys.');
            return [];
        }

        const surveysRef = collection(db, 'banks', bankId, 'leaseRenewalSurveys');

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');
        const isBankUser = !designation;

        if (isMIS || isBankUser) {
            const allSnap = await getDocs(surveysRef);
            addSnapshotToMap(allSnap, bankId);
        } else {
            // Strict data isolation
            const createdSnap = await getDocs(query(surveysRef, where('createdBy', '==', uid)));
            addSnapshotToMap(createdSnap);

            const assignedSnap = await getDocs(query(surveysRef, where('assignedTo', '==', uid)));
            addSnapshotToMap(assignedSnap);

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    for (const managedId of managedIds) {
                        const managedCreated = await getDocs(query(surveysRef, where('createdBy', '==', managedId)));
                        addSnapshotToMap(managedCreated);

                        const managedAssigned = await getDocs(query(surveysRef, where('assignedTo', '==', managedId)));
                        addSnapshotToMap(managedAssigned);
                    }
                } catch (error) {
                    console.error('Error fetching managed PSO surveys:', error);
                }
            }
        }

        const surveysList: LeaseRenewalSurvey[] = Array.from(surveysMap.values());
        return sortSurveys(surveysList);
    } catch (error) {
        console.error('Error fetching lease renewal surveys:', error);
        return [];
    }
}

export async function createLeaseRenewalSurvey(bankId: string, data: Omit<LeaseRenewalSurvey, 'id' | 'createdAt'>): Promise<string> {
    try {
        const docRef = await addDoc(collection(db, 'banks', bankId, 'leaseRenewalSurveys'), {
            ...data,
            createdAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating lease renewal survey request:', error);
        throw error;
    }
}

export async function updateLeaseRenewalSurvey(surveyId: string, bankId: string, updates: Partial<LeaseRenewalSurvey>): Promise<void> {
    try {
        const surveyRef = doc(db, 'banks', bankId, 'leaseRenewalSurveys', surveyId);
        await updateDoc(surveyRef, {
            ...updates,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating lease renewal survey:', error);
        throw error;
    }
}

export async function deleteLeaseRenewalSurvey(bankId: string, surveyId: string): Promise<void> {
    try {
        const { deleteDoc } = await import('firebase/firestore');
        const surveyRef = doc(db, 'banks', bankId, 'leaseRenewalSurveys', surveyId);
        await deleteDoc(surveyRef);
    } catch (error) {
        console.error('Error deleting lease renewal survey:', error);
        throw error;
    }
}

export function generateLeaseSurveyId(): string {
    const rand = Math.floor(10000 + Math.random() * 90000);
    return `LRS-${rand}`;
}
